import Axios from 'axios';
import React, {
createContext, useContext, useEffect, useMemo, useState } from
'react';
import dynamic from 'next/dynamic';
import useSWRImmutable from 'swr/immutable';

import LoadingOverlay from '@strongio/pretrained-dashboard/components/LoadingOverlay';
import { publishEvent } from '@strongio/pretrained-dashboard/events';

const GettingStarted = dynamic(
() => import('@strongio/pretrained-dashboard/views/GettingStarted'),
{ loading: LoadingOverlay });


const ErrorView = dynamic(
() => import('@strongio/pretrained-dashboard/views/ErrorView'),
{ loading: LoadingOverlay });


const TEAM_KEY = 'team';
const TOKEN_KEY = 'accessToken';
const REQUIRE_MFA_KEY = 'requireMfa';

const isBrowser = typeof localStorage !== 'undefined';

let savedTeam = '';
let savedAccessToken = '';
let savedRequireMfa = false;

if (isBrowser) {
  savedAccessToken = localStorage.getItem(TOKEN_KEY) || '';
  savedRequireMfa = !!localStorage.getItem(REQUIRE_MFA_KEY);
  savedTeam = localStorage.getItem(TEAM_KEY) || '';
}

if (savedTeam) {
  Axios.defaults.headers.common['X-Team'] = savedTeam;
}

if (savedAccessToken) {
  Axios.defaults.headers.common.Authorization = `Bearer ${savedAccessToken}`;
}

Axios.interceptors.response.use(undefined, (err) => new Promise(() => {
  if (err.response && err.response.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    if (window.location.pathname.toLowerCase() !== '/login') {
      window.location.href = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    } else {
      window.location.reload();
    }
  }
  throw err;
}));

const setTeam = (teamSlug) => {
  if (teamSlug) {
    localStorage.setItem(TEAM_KEY, teamSlug);
    Axios.defaults.headers.common['X-Team'] = teamSlug;
    savedTeam = teamSlug;
  } else {
    localStorage.removeItem(TEAM_KEY);
    delete Axios.defaults.headers.common['X-Team'];
    savedTeam = null;
  }
};

const authActions = {
  loadingUser: true,
  setToken: ({ accessToken, requireMfa }) => {
    if (requireMfa) {
      localStorage.setItem(REQUIRE_MFA_KEY, '1');
    } else {
      localStorage.removeItem(REQUIRE_MFA_KEY);
    }

    localStorage.setItem(TOKEN_KEY, accessToken);
    Axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    savedAccessToken = accessToken;
    savedRequireMfa = requireMfa;
  },

  removeToken: () => {
    localStorage.removeItem(TOKEN_KEY);
    delete Axios.defaults.headers.common.Authorization;
    savedAccessToken = null;
  },

  switchTeam: (teamSlug) => {
    setTeam(teamSlug);
    window.location = '/';
  },

  logout: () => {
    Axios.post('/api/user/logout').then(() => {
      authActions.removeToken();
      window.location = '/login';
    });
  },

  autoSelectTeam: (teams) => {
    let changed = false;
    if (savedTeam && teams.filter((t) => t.slug === savedTeam).length === 0) {
      setTeam();
      changed = true;
    }

    if (!savedTeam && teams.length > 0) {
      setTeam(teams[0].slug);
      changed = true;
    }

    if (changed) {
      window.location.reload();
    }
  },

  getTeam: () => savedTeam,
  isAuthenticated: () => !!savedAccessToken,
  isMfaRequired: () => savedRequireMfa,
  hasPermission: () => false };


const AuthContext = createContext(authActions);
export const useAuth = () => useContext(AuthContext);
export const redirectForAuth = (noAuth, noMfa) => {
  if (isBrowser) {
    let requiresMfa = true;
    let requireAuth = true;

    if (noMfa) {
      requiresMfa = false;
    }

    if (noAuth) {
      requiresMfa = false;
      requireAuth = false;
    }

    if (requiresMfa && authActions.isMfaRequired()) {
      return `/login/mfa?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    }

    if (requireAuth && !authActions.isAuthenticated()) {
      return `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    }
  }

  return '';
};

export function AuthProvider({ children, noAuth }) {
  const authenticated = savedAccessToken && !savedRequireMfa;
  const [loading, setLoading] = useState(true);
  const [showCreateTeamDialogue, setShowCreateTeamDialogue] = useState(false);

  const { data: userProfile, error: userProfileError } = useSWRImmutable(
  authenticated ? '/api/user/profile' : undefined);

  const { data: permissions, error: permissionsError } = useSWRImmutable(
  savedTeam && authenticated ? '/api/user/permissions' : undefined);


  // Initial load should always be for loader.
  // Otherwise, HTML by server and client will not match
  useEffect(() => {
    setLoading(noAuth ? authenticated && !userProfile : !permissions || !userProfile);
  }, [authenticated, noAuth, permissions, userProfile]);

  useEffect(() => {
    if (typeof userProfile !== 'undefined') {
      if (userProfile.teams.length === 0) {
        setShowCreateTeamDialogue(true);
        setLoading(false);
      } else {
        authActions.autoSelectTeam(userProfile.teams);
      }

      publishEvent('profile-updated', userProfile.profile);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  const providerValue = useMemo(() => {
    const actions = { ...authActions };
    actions.hasPermission = (permission) => permissions && permissions.indexOf(permission) >= 0;
    actions.getUser = () => userProfile && userProfile.profile;
    actions.getTeams = () => userProfile && userProfile.teams;
    actions.loadingUser = false;
    return actions;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, permissions]);

  if (loading) {
    children = /*#__PURE__*/React.createElement(LoadingOverlay, null);
    if (userProfileError || permissionsError) {
      children = /*#__PURE__*/React.createElement(ErrorView, { error: userProfileError || permissionsError });
    }
  } else if (showCreateTeamDialogue) {
    children = /*#__PURE__*/
    React.createElement(GettingStarted, { onSuccess: () => {
        window.location.reload();
        setShowCreateTeamDialogue(false);
      } });


  }

  return /*#__PURE__*/(
    React.createElement(AuthContext.Provider, { value: providerValue },
    children));


}